import type { OutOfScopeValue } from './attribute-scope';
import { addDays, diffDays, weekStart, type ISODate } from './dates';

/**
 * Project analysis (Projektanalyse). Pure functions: the server delivers tasks and weekly hours, the web client
 * and the Excel export compute the same figures from them. Definitions are documented in docs/KPI.md.
 *
 * - Progress of a group = finished tasks / tasks (Forma "completed" or "closed", platform task closed). With the
 *   project setting "progressBy = plan" the planned hours of the finished tasks / planned hours of all tasks with a
 *   plan are used instead (tasks without a plan do not count there).
 * - Schedule state of a group (Terminlage): late when at least 3, or at least a fifth, of the open tasks with a due
 *   date are overdue; at risk with one overdue task or an open task due within 7 days that has not started; otherwise
 *   on track; "none" without due dates.
 * - Hours: "actual" are the hours of the selected period, "lifetime" all hours ever booked on the tasks (used for
 *   the plan comparison, because a plan covers the whole task).
 * - Hidden (archived) tasks do not count as tasks; hours booked on them still count as hours.
 */

/* ------------------------------------------------------------------ */
/* Stages and floors                                                    */
/* ------------------------------------------------------------------ */

export const TASK_STAGES = ['open', 'progress', 'review', 'done'] as const;
export type TaskStage = (typeof TASK_STAGES)[number];

/** Stage of a task for the progress charts: open, in progress, in review, finished. */
export function taskStage(status: string, statusGroup: string): TaskStage {
  if (statusGroup !== 'active') return 'done';
  if (status === 'in_review') return 'review';
  if (status === 'draft' || status === 'open') return 'open';
  return 'progress';
}

/** Values that mean "applies to the whole project" rather than one floor or building. */
const GENERAL = new Set(['allgemein', 'gesamt', 'alle', 'general', 'all', 'genel', 'tumu', 'hepsi', '-', '--', 'n/a', 'na', 'keine', 'none', 'ohne', 'sonstige', 'sonstiges']);

function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ß/g, 'ss')
    .replace(/[\u2013\u2014]/g, '-')
    .trim();
}

export function isGeneralValue(label: string | null | undefined): boolean {
  if (!label) return true;
  return GENERAL.has(norm(label).replace(/\s+/g, ' '));
}

/**
 * Height rank of a floor name, or null when the name is not a floor (e.g. "Technikzentrale", "Allgemein").
 * Basements are negative, the ground floor 0, upper floors 1, 2, ..., an attic 500 and the roof 1000.
 * German, English and Turkish names: "2.UG", "UG", "KG", "EG", "1. OG", "OG 3", "Level 2", "L2", "B1", "GF",
 * "Zemin kat", "3. Kat", "Bodrum", "DG", "Dach", "Roof", "Çatı", "ZG" (mezzanine 0.5) and plain numbers.
 */
export function floorRank(label: string | null | undefined): number | null {
  if (!label) return null;
  const s = norm(label).replace(/\s+/g, ' ');
  if (!s || GENERAL.has(s)) return null;
  const c = s.replace(/[\s._]/g, '');
  const num = (x: string | undefined, d = 1) => (x ? Number(x) : d);
  let m: RegExpExecArray | null;
  if ((m = /^([+-]?\d{1,3})$/.exec(c))) return Number(m[1]);
  // roof and attic
  if (/^(dach|dachflache|dachaufsicht|roof|rooftop|cati|catiust|dachzentrale|technikdach)$/.test(c)) return 1000;
  if (/^(dg|dachgeschoss|attika|attikageschoss|penthouse|catikati|cati-kati|attic)$/.test(c)) return 500;
  // ground floor and mezzanine
  if (/^(eg|erdgeschoss|gf|groundfloor|ground|zemin|zeminkat|l0|e0|00|level0|lv0)$/.test(c)) return 0;
  if (/^(zg|zwischengeschoss|mezzanine|mezz|asmakat|asma)$/.test(c)) return 0.5;
  // basements: 2.UG, UG2, 2UG, UG, KG, Keller, TG, B1, Level -1, 1.Bodrum, Bodrum 2, -1.Kat
  if ((m = /^(\d{1,2})(ug|untergeschoss|kg|kellergeschoss|keller|tg|tiefgarage|b|basement|bodrum|bodrumkat|bk)$/.exec(c))) return -num(m[1]);
  if ((m = /^(ug|untergeschoss|kg|kellergeschoss|keller|tg|tiefgarage|b|basement|bodrum|bodrumkat|bk|lg|lowerground)(\d{1,2})?$/.exec(c))) return -num(m[2]);
  if ((m = /^(level|lv|l|ebene|kat)-(\d{1,2})$/.exec(c))) return -num(m[2]);
  if ((m = /^-(\d{1,2})(kat|og|l|level)?$/.exec(c))) return -num(m[1]);
  // upper floors: 1.OG, OG1, 1OG, 1. Obergeschoss, 1. Stock, 1. Etage, Level 1, L1, 1st floor, 1. Kat, 1F
  if ((m = /^(\d{1,3})(og|obergeschoss|stock|etage|ebene|floor|fl|f|kat|katlar|l|level|st|nd|rd|th|stfloor|ndfloor|rdfloor|thfloor)$/.exec(c))) return num(m[1]);
  if ((m = /^(og|obergeschoss|stock|etage|ebene|level|lv|l|floor|kat)\+?(\d{1,3})$/.exec(c))) return num(m[2]);
  return null;
}

/** Natural order for values like "KG 410", "LP 2", "LP 10". */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' });
}

/* ------------------------------------------------------------------ */
/* Configuration                                                        */
/* ------------------------------------------------------------------ */

/**
 * Where a part of the building comes from: a custom field of Forma or a level (tier) of the Forma location tree. Forma
 * defines locations in up to three tiers (Project Admin > Locations), e.g. building > floor > zone (round 5, request 13).
 */
export type AnalysisSource = { kind: 'attr'; id: string } | { kind: 'loc'; level: 1 | 2 | 3 } | { kind: 'none' };

export interface AnalysisConfig {
  /** where the floor of a task comes from (custom field or level of the Forma location tree) */
  floor: AnalysisSource;
  /** where the building (Bauteil) of a task comes from: the location tree, a custom field only without a tree */
  building: AnalysisSource;
  /**
   * zones of a floor (third tier of the location tree, round 5): with a zone source the analysis shows one floor with its
   * zones and the work left in each
   */
  zone?: AnalysisSource;
  /** custom field with the trade (Gewerk, Kostengruppe) */
  trade: string | null;
  /** custom field with the work stage (Leistungsphase) */
  phase: string | null;
  /** progress by number of tasks (default) or by planned hours */
  progressBy: 'count' | 'plan';
}

export const EMPTY_ANALYSIS_CONFIG: AnalysisConfig = { floor: { kind: 'none' }, building: { kind: 'none' }, zone: { kind: 'none' }, trade: null, phase: null, progressBy: 'count' };

export interface AnalysisFieldDef {
  id: string;
  title: string;
  mirrored: boolean;
}

export interface AnalysisLocation {
  id: string;
  parentId: string | null;
  name: string;
  depth: number;
}

const FLOOR_FIELD = /(geschoss|etage|ebene|stockwerk|floor|storey|level|\bkat\b)/i;
const BUILDING_FIELD = /(bauteil|geb[aä]ude|\bhaus\b|teilprojekt|bauabschnitt|building|\bblock\b|\bblok\b|\bbina\b)/i;
const TRADE_FIELD = /(gewerk|kostengruppe|disziplin|fachbereich|\btrade\b|discipline|disiplin)/i;
const PHASE_FIELD = /(leistungsphase|\blph?\b|\bphase\b|\bfaz\b|\bhoai\b)/i;

/**
 * Suggested configuration. The Forma location tree comes first (round 5, request 13: locations replace the custom field
 * "Teilprojekt"): when its second tier holds floor names, buildings are the first tier, floors the second and zones the
 * third (when the tree has one). Without such a tree a field named like "Geschoss" gives the floors and a field like
 * "Bauteil"/"Teilprojekt" the buildings; "Gewerk" and "Leistungsphase" always come from their fields.
 */
export function detectAnalysisConfig(defs: AnalysisFieldDef[], locations: AnalysisLocation[]): AnalysisConfig {
  const fields = defs.filter((d) => d.mirrored);
  const find = (re: RegExp, except: (string | null)[] = []) => fields.find((d) => re.test(d.title) && !except.includes(d.id))?.id ?? null;
  const phase = find(PHASE_FIELD);
  const trade = find(TRADE_FIELD, [phase]);
  const floorAttr = find(FLOOR_FIELD, [phase, trade]);
  const buildingAttr = find(BUILDING_FIELD, [phase, trade, floorAttr]);
  const share = (nodes: AnalysisLocation[]) => (nodes.length ? nodes.filter((n) => floorRank(n.name) !== null).length / nodes.length : 0);
  let floor: AnalysisSource = floorAttr ? { kind: 'attr', id: floorAttr } : { kind: 'none' };
  let building: AnalysisSource = buildingAttr ? { kind: 'attr', id: buildingAttr } : { kind: 'none' };
  let zone: AnalysisSource = { kind: 'none' };
  if (locations.length) {
    const level1 = locations.filter((n) => !n.parentId);
    const level2 = locations.filter((n) => n.parentId && level1.some((p) => p.id === n.parentId));
    const level3 = locations.filter((n) => n.parentId && level2.some((p) => p.id === n.parentId));
    if (level2.length && share(level2) >= 0.4) {
      floor = { kind: 'loc', level: 2 };
      building = { kind: 'loc', level: 1 };
      if (level3.length) zone = { kind: 'loc', level: 3 };
    } else if (!floorAttr && share(level1) >= 0.5) {
      floor = { kind: 'loc', level: 1 };
      if (level2.length) zone = { kind: 'loc', level: 2 };
    }
  }
  return { floor, building, zone, trade, phase, progressBy: 'count' };
}

/** Stored configuration completed with the suggestion where a part is missing. */
export function resolveAnalysisConfig(stored: Partial<AnalysisConfig> | null | undefined, suggestion: AnalysisConfig): AnalysisConfig {
  const s = stored ?? {};
  return {
    floor: s.floor ?? suggestion.floor,
    building: s.building ?? suggestion.building,
    zone: s.zone ?? suggestion.zone ?? { kind: 'none' },
    trade: s.trade !== undefined ? s.trade : suggestion.trade,
    phase: s.phase !== undefined ? s.phase : suggestion.phase,
    progressBy: s.progressBy === 'plan' ? 'plan' : 'count',
  };
}

/* ------------------------------------------------------------------ */
/* Data                                                                 */
/* ------------------------------------------------------------------ */

export interface AnalysisTask {
  id: string;
  ref: string;
  title: string;
  source: string;
  status: string;
  statusGroup: string;
  categoryTitle: string | null;
  typeTitle: string | null;
  manualCategory: string | null;
  /** names of the location tree levels (tiers 1 to 3 of Forma) and the full path */
  loc1: string | null;
  loc2: string | null;
  loc3?: string | null;
  locationPath: string | null;
  /** custom field values taken over from Forma: field id -> text */
  attributes: Record<string, string>;
  plannedMinutes: number | null;
  startDate: ISODate | null;
  dueDate: ISODate | null;
  createdOn: ISODate | null;
  doneOn: ISODate | null;
  archived: boolean;
  formaIssueId: string | null;
  /** values outside the fields' scope of the project (not in `attributes`, listed by the analysis; request 20) */
  outOfScope?: OutOfScopeValue[];
}

/** Hours of one person on one task in one week (Monday). Money only for users with the right to see it. */
export interface AnalysisHours {
  taskId: string;
  personId: string;
  /** Monday of the week of the bookings (weekly charts) */
  week: ISODate;
  /**
   * Day of the bookings (round 5): the server sums hours per day, so a period that starts or ends inside a week counts
   * only its days (DAT-06, review of 29.10.2026). Without it (older callers) the period is matched by week.
   */
  date?: ISODate;
  minutes: number;
  billable: number;
  costCents?: number | null;
  salesCents?: number | null;
}

export interface AnalysisPeriod {
  /** first and last day of the period for hours; null = whole project */
  from: ISODate | null;
  to: ISODate | null;
}

export interface AnalysisOptions {
  today: ISODate;
  period: AnalysisPeriod;
  config: AnalysisConfig;
  /** floors of each building from the fields' scope of the project: drawn also without tasks (request 20) */
  buildingFloors?: Record<string, string[]>;
}

export const NONE = '__none__';

/** Dimensions: building, floor, zone, trade, phase, category, type, stage, source, loc1, loc2, loc3, attr:<id>, person. */
export type AnalysisDim = string;

/** Value of a task in a dimension ('' for "no value"). Person is not a task dimension (see filterData). */
export function taskDimValue(t: AnalysisTask, dim: AnalysisDim, config: AnalysisConfig): string {
  const fromSource = (s: AnalysisSource): string => {
    if (s.kind === 'attr') return t.attributes[s.id] ?? '';
    if (s.kind === 'loc') return (s.level === 1 ? t.loc1 : s.level === 2 ? t.loc2 : t.loc3) ?? '';
    return '';
  };
  switch (dim) {
    case 'building':
      return fromSource(config.building);
    case 'floor':
      return fromSource(config.floor);
    case 'zone':
      return config.zone ? fromSource(config.zone) : '';
    case 'trade':
      return config.trade ? t.attributes[config.trade] ?? '' : '';
    case 'phase':
      return config.phase ? t.attributes[config.phase] ?? '' : '';
    case 'category':
      return t.categoryTitle ?? '';
    case 'type':
      return t.typeTitle ?? '';
    case 'stage':
      return taskStage(t.status, t.statusGroup);
    case 'source':
      return t.source;
    case 'manualCategory':
      return t.manualCategory ?? '';
    case 'loc1':
      return t.loc1 ?? '';
    case 'loc2':
      return t.loc2 ?? '';
    case 'loc3':
      return t.loc3 ?? '';
    default:
      if (dim.startsWith('attr:')) return t.attributes[dim.slice(5)] ?? '';
      return '';
  }
}

export type AnalysisFilters = Record<AnalysisDim, string>;

/**
 * Applies the filters (dimension -> value, NONE for "without value", '' for all). The person filter keeps the hours
 * of that person and the tasks the person booked on.
 */
export function filterData(tasks: AnalysisTask[], hours: AnalysisHours[], filters: AnalysisFilters, config: AnalysisConfig) {
  const active = Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined);
  const person = active.find(([k]) => k === 'person')?.[1];
  const taskFilters = active.filter(([k]) => k !== 'person');
  let ts = tasks.filter((t) => taskFilters.every(([k, v]) => (v === NONE ? taskDimValue(t, k, config) === '' : taskDimValue(t, k, config) === v)));
  let hs = hours;
  if (person) {
    hs = hs.filter((h) => h.personId === person);
    const booked = new Set(hs.map((h) => h.taskId));
    ts = ts.filter((t) => booked.has(t.id));
  }
  const ids = new Set(ts.map((t) => t.id));
  return { tasks: ts, hours: hs.filter((h) => ids.has(h.taskId)) };
}

/* ------------------------------------------------------------------ */
/* Statistics of a group of tasks                                       */
/* ------------------------------------------------------------------ */

export type ScheduleState = 'ok' | 'risk' | 'late' | 'none';

export interface GroupStat {
  key: string;
  label: string;
  tasks: number;
  open: number;
  progress: number;
  review: number;
  done: number;
  /** open tasks with a due date before today */
  overdue: number;
  /** open tasks due within the next 7 days */
  dueSoon: number;
  /** open tasks with a due date */
  withDue: number;
  plannedMinutes: number;
  plannedTasks: number;
  donePlannedMinutes: number;
  /** all hours booked on the finished tasks with a plan (Plan-Ist-Quote) */
  doneActualMinutes: number;
  /** hours in the selected period */
  actualMinutes: number;
  billableMinutes: number;
  /** all hours booked on the tasks (whole lifetime) */
  lifetimeMinutes: number;
  /** all hours on the tasks with a plan */
  plannedTasksActualMinutes: number;
  costCents: number | null;
  salesCents: number | null;
  costMissingMinutes: number;
  /** finished / tasks */
  completion: number;
  /** planned hours of finished tasks / planned hours (tasks with a plan) */
  completionPlan: number;
  /** hours on planned tasks / planned hours */
  planUse: number;
  /** planned hours of finished tasks / hours booked on them */
  planRatio: number;
  schedule: ScheduleState;
  /** average days from creation to completion of the finished tasks */
  avgCloseDays: number;
  /** finished on or before the due date / finished with a due date */
  onTime: number;
}

/** The hours lie in the period: by their day when known, otherwise by their week. */
export function hoursInPeriod(h: Pick<AnalysisHours, 'week' | 'date'>, p: AnalysisPeriod): boolean {
  if (h.date) return (!p.from || h.date >= p.from) && (!p.to || h.date <= p.to);
  if (p.from && h.week < weekStart(p.from)) return false;
  if (p.to && h.week > p.to) return false;
  return true;
}

export function scheduleState(overdue: number, withDue: number, notStartedDueSoon: number): ScheduleState {
  if (withDue === 0) return 'none';
  if (overdue >= 3 || (overdue > 0 && overdue / withDue >= 0.2)) return 'late';
  if (overdue > 0 || notStartedDueSoon > 0) return 'risk';
  return 'ok';
}

/** Statistics of a set of tasks with their hours (hours of other tasks are ignored). */
export function groupStat(key: string, label: string, tasks: AnalysisTask[], hours: AnalysisHours[], opts: AnalysisOptions, withMoney: boolean): GroupStat {
  const visible = tasks.filter((t) => !t.archived);
  const ids = new Set(tasks.map((t) => t.id));
  const perTask = new Map<string, number>();
  let actual = 0;
  let billable = 0;
  let lifetime = 0;
  let cost = 0;
  let sales = 0;
  let costMissing = 0;
  for (const h of hours) {
    if (!ids.has(h.taskId)) continue;
    lifetime += h.minutes;
    perTask.set(h.taskId, (perTask.get(h.taskId) ?? 0) + h.minutes);
    if (!hoursInPeriod(h, opts.period)) continue;
    actual += h.minutes;
    billable += h.billable;
    if (withMoney) {
      if (h.costCents === null || h.costCents === undefined) costMissing += h.minutes;
      else cost += h.costCents;
      sales += h.salesCents ?? 0;
    }
  }
  const s = { open: 0, progress: 0, review: 0, done: 0 };
  let overdue = 0;
  let dueSoon = 0;
  let notStartedSoon = 0;
  let withDue = 0;
  let planned = 0;
  let plannedTasks = 0;
  let donePlanned = 0;
  let doneActual = 0;
  let plannedActual = 0;
  const closeDays: number[] = [];
  let doneWithDue = 0;
  let doneOnTime = 0;
  const soon = addDays(opts.today, 7);
  for (const t of visible) {
    const stage = taskStage(t.status, t.statusGroup);
    s[stage]++;
    const booked = perTask.get(t.id) ?? 0;
    if (t.plannedMinutes && t.plannedMinutes > 0) {
      planned += t.plannedMinutes;
      plannedTasks++;
      plannedActual += booked;
      if (stage === 'done') {
        donePlanned += t.plannedMinutes;
        doneActual += booked;
      }
    }
    if (stage === 'done') {
      if (t.createdOn && t.doneOn) closeDays.push(Math.max(0, diffDays(t.doneOn, t.createdOn)));
      if (t.dueDate && t.doneOn) {
        doneWithDue++;
        if (t.doneOn <= t.dueDate) doneOnTime++;
      }
      continue;
    }
    if (!t.dueDate) continue;
    withDue++;
    if (t.dueDate < opts.today) overdue++;
    else if (t.dueDate <= soon) {
      dueSoon++;
      if (stage === 'open') notStartedSoon++;
    }
  }
  const n = visible.length;
  return {
    key,
    label,
    tasks: n,
    ...s,
    overdue,
    dueSoon,
    withDue,
    plannedMinutes: planned,
    plannedTasks,
    donePlannedMinutes: donePlanned,
    doneActualMinutes: doneActual,
    actualMinutes: actual,
    billableMinutes: billable,
    lifetimeMinutes: lifetime,
    plannedTasksActualMinutes: plannedActual,
    costCents: withMoney ? cost : null,
    salesCents: withMoney ? sales : null,
    costMissingMinutes: withMoney ? costMissing : 0,
    completion: n ? s.done / n : Number.NaN,
    completionPlan: planned ? donePlanned / planned : Number.NaN,
    planUse: planned ? plannedActual / planned : Number.NaN,
    planRatio: doneActual ? donePlanned / doneActual : Number.NaN,
    schedule: scheduleState(overdue, withDue, notStartedSoon),
    avgCloseDays: closeDays.length ? closeDays.reduce((a, b) => a + b, 0) / closeDays.length : Number.NaN,
    onTime: doneWithDue ? doneOnTime / doneWithDue : Number.NaN,
  };
}

/** Progress of a group by the configured measure (tasks or planned hours). */
export function progressOf(s: GroupStat, by: AnalysisConfig['progressBy']): number {
  return by === 'plan' ? s.completionPlan : s.completion;
}

function dimSort(dim: AnalysisDim): (a: string, b: string) => number {
  return (a, b) => {
    if (a === b) return 0;
    if (a === '') return 1;
    if (b === '') return -1;
    if (dim === 'floor') {
      const ra = floorRank(a);
      const rb = floorRank(b);
      if (ra !== null && rb !== null) return ra - rb;
      if (ra !== null) return -1;
      if (rb !== null) return 1;
    }
    if (dim === 'stage') return TASK_STAGES.indexOf(a as TaskStage) - TASK_STAGES.indexOf(b as TaskStage);
    return naturalCompare(a, b);
  };
}

/**
 * Statistics per value of a dimension (sorted: floors by height, stages in order, others naturally; "no value"
 * last). Person: hours of each person and the tasks they booked on.
 */
export function groupBy(tasks: AnalysisTask[], hours: AnalysisHours[], dim: AnalysisDim, opts: AnalysisOptions, withMoney: boolean): GroupStat[] {
  if (dim === 'person') {
    const byPerson = new Map<string, AnalysisHours[]>();
    for (const h of hours) {
      const list = byPerson.get(h.personId) ?? [];
      list.push(h);
      byPerson.set(h.personId, list);
    }
    const taskMap = new Map(tasks.map((t) => [t.id, t]));
    return [...byPerson.entries()]
      .map(([pid, hs]) => {
        const own = [...new Set(hs.map((h) => h.taskId))].map((id) => taskMap.get(id)).filter((t): t is AnalysisTask => !!t);
        return groupStat(pid, pid, own, hs, opts, withMoney);
      })
      .sort((a, b) => b.actualMinutes - a.actualMinutes || b.lifetimeMinutes - a.lifetimeMinutes);
  }
  const groups = new Map<string, AnalysisTask[]>();
  for (const t of tasks) {
    const v = taskDimValue(t, dim, opts.config);
    const list = groups.get(v) ?? [];
    list.push(t);
    groups.set(v, list);
  }
  const sort = dimSort(dim);
  return [...groups.entries()]
    .sort(([a], [b]) => sort(a, b))
    .map(([v, ts]) => groupStat(v === '' ? NONE : v, v, ts, hours, opts, withMoney))
    .filter((g) => g.tasks > 0 || g.lifetimeMinutes > 0);
}

/* ------------------------------------------------------------------ */
/* Building view                                                        */
/* ------------------------------------------------------------------ */

export interface BuildingFloor extends GroupStat {
  rank: number;
}

export interface BuildingModel {
  key: string;
  label: string;
  /** floors from the bottom (lowest basement) to the top (roof) */
  floors: BuildingFloor[];
  /** tasks of the building without a floor or with a general value ("Allgemein") */
  general: GroupStat | null;
  total: GroupStat;
}

export interface BuildingView {
  buildings: BuildingModel[];
  /** buildings or areas without floors (e.g. "Außenanlagen"), and tasks without a building */
  areas: GroupStat[];
  /** no floor source configured or no task has a floor */
  empty: boolean;
}

/**
 * Buildings with their floors for the schematic view. A building value whose tasks have no recognisable floor is
 * shown as an area next to the buildings. Without a building source all tasks form one building.
 */
export function buildingView(tasks: AnalysisTask[], hours: AnalysisHours[], opts: AnalysisOptions, withMoney: boolean, projectLabel = ''): BuildingView {
  const cfg = opts.config;
  if (cfg.floor.kind === 'none') return { buildings: [], areas: [], empty: true };
  const byBuilding = new Map<string, AnalysisTask[]>();
  for (const t of tasks) {
    const b = cfg.building.kind === 'none' ? projectLabel || '' : taskDimValue(t, 'building', cfg);
    const list = byBuilding.get(b) ?? [];
    list.push(t);
    byBuilding.set(b, list);
  }
  const buildings: BuildingModel[] = [];
  const areas: GroupStat[] = [];
  for (const [b, ts] of [...byBuilding.entries()].sort(([a], [c]) => dimSort('building')(a, c))) {
    const floors = new Map<string, AnalysisTask[]>();
    const general: AnalysisTask[] = [];
    for (const t of ts) {
      const f = taskDimValue(t, 'floor', cfg);
      if (floorRank(f) === null) general.push(t);
      else {
        const list = floors.get(f) ?? [];
        list.push(t);
        floors.set(f, list);
      }
    }
    // floors of the building without a task yet (from the scope of the project): drawn empty, so the picture shows the
    // whole building and not only the floors somebody worked on
    for (const f of opts.buildingFloors?.[b] ?? []) if (!floors.has(f) && floorRank(f) !== null) floors.set(f, []);
    const total = groupStat(b || NONE, b, ts, hours, opts, withMoney);
    const isBuilding = floors.size > 0 && (cfg.building.kind === 'none' || !isGeneralValue(b));
    if (!isBuilding) {
      if (total.tasks > 0 || total.lifetimeMinutes > 0) areas.push(total);
      continue;
    }
    buildings.push({
      key: b || NONE,
      label: b,
      floors: [...floors.entries()]
        .map(([f, list]) => ({ ...groupStat(f, f, list, hours, opts, withMoney), rank: floorRank(f)! }))
        .sort((x, y) => x.rank - y.rank),
      general: general.length ? groupStat(`${b || NONE}|general`, '', general, hours, opts, withMoney) : null,
      total,
    });
  }
  return { buildings, areas, empty: buildings.length === 0 };
}

/**
 * One floor with its zones (round 5, request 13): the tasks of a building and floor grouped by the zone source (third
 * tier of the location tree). Each zone shows its progress and the work left (open tasks, planned hours not done); tasks
 * of the floor without a zone are listed apart. Empty without a zone source.
 */
export interface ZoneView {
  zones: (GroupStat & { open: number })[];
  /** tasks of the floor without a zone */
  general: (GroupStat & { open: number }) | null;
  total: GroupStat;
}

export function zoneView(tasks: AnalysisTask[], hours: AnalysisHours[], opts: AnalysisOptions, withMoney: boolean, building: string | null, floor: string): ZoneView | null {
  const cfg = opts.config;
  if (!cfg.zone || cfg.zone.kind === 'none') return null;
  const onFloor = tasks.filter((t) => taskDimValue(t, 'floor', cfg) === floor && (building === null || cfg.building.kind === 'none' || taskDimValue(t, 'building', cfg) === building));
  const open = (list: AnalysisTask[]) => list.filter((t) => t.statusGroup === 'active').length;
  const byZone = new Map<string, AnalysisTask[]>();
  const none: AnalysisTask[] = [];
  for (const t of onFloor) {
    const z = taskDimValue(t, 'zone', cfg);
    if (!z) none.push(t);
    else byZone.set(z, [...(byZone.get(z) ?? []), t]);
  }
  return {
    zones: [...byZone.entries()].sort(([a], [b]) => naturalCompare(a, b)).map(([z, list]) => ({ ...groupStat(z, z, list, hours, opts, withMoney), open: open(list) })),
    general: none.length ? { ...groupStat(NONE, '', none, hours, opts, withMoney), open: open(none) } : null,
    total: groupStat('all', floor, onFloor, hours, opts, withMoney),
  };
}

/* ------------------------------------------------------------------ */
/* Matrix (e.g. floor x trade)                                          */
/* ------------------------------------------------------------------ */

export interface MatrixModel {
  rows: { key: string; label: string }[];
  cols: { key: string; label: string }[];
  /** cells[row][col]; null where no task exists */
  cells: (GroupStat | null)[][];
  rowTotals: GroupStat[];
  colTotals: GroupStat[];
}

export function matrixModel(tasks: AnalysisTask[], hours: AnalysisHours[], rowDim: AnalysisDim, colDim: AnalysisDim, opts: AnalysisOptions, withMoney: boolean): MatrixModel {
  const rowsStat = groupBy(tasks, hours, rowDim, opts, withMoney).filter((g) => g.tasks > 0);
  const colsStat = groupBy(tasks, hours, colDim, opts, withMoney).filter((g) => g.tasks > 0);
  const valueOf = (t: AnalysisTask, dim: AnalysisDim) => taskDimValue(t, dim, opts.config) || NONE;
  // floors are listed from the top, like the building; values that are no floor ("Allgemein", none) follow
  if (rowDim === 'floor') {
    const ranked = rowsStat.filter((r) => floorRank(r.label) !== null).reverse();
    const rest = rowsStat.filter((r) => floorRank(r.label) === null);
    rowsStat.splice(0, rowsStat.length, ...ranked, ...rest);
  }
  const index = new Map<string, AnalysisTask[]>();
  for (const t of tasks) {
    const k = `${valueOf(t, rowDim)}\u0000${valueOf(t, colDim)}`;
    const list = index.get(k) ?? [];
    list.push(t);
    index.set(k, list);
  }
  return {
    rows: rowsStat.map((r) => ({ key: r.key, label: r.label })),
    cols: colsStat.map((c) => ({ key: c.key, label: c.label })),
    cells: rowsStat.map((r) =>
      colsStat.map((c) => {
        const list = index.get(`${r.key}\u0000${c.key}`);
        if (!list?.length) return null;
        const s = groupStat(`${r.key}|${c.key}`, '', list, hours, opts, withMoney);
        return s.tasks > 0 ? s : null;
      }),
    ),
    rowTotals: rowsStat,
    colTotals: colsStat,
  };
}

/* ------------------------------------------------------------------ */
/* Course over time                                                     */
/* ------------------------------------------------------------------ */

export interface TimelinePoint {
  week: ISODate;
  /** tasks created / finished in the week */
  created: number;
  done: number;
  /** tasks existing / finished at the end of the week */
  total: number;
  finished: number;
  minutes: number;
  billable: number;
}

/** Weekly course: created and finished tasks (cumulated as burn-up) and booked hours, up to the current week. */
export function timeline(tasks: AnalysisTask[], hours: AnalysisHours[], opts: AnalysisOptions, maxWeeks = 52): TimelinePoint[] {
  const visible = tasks.filter((t) => !t.archived);
  const end = weekStart(opts.period.to && opts.period.to < opts.today ? opts.period.to : opts.today);
  let start = weekStart(opts.period.from ?? addDays(end, -7 * (maxWeeks - 1)));
  if (!opts.period.from) {
    const firsts = [...visible.map((t) => t.createdOn).filter((d): d is string => !!d), ...hours.map((h) => h.week)].sort();
    if (firsts.length && weekStart(firsts[0]) > start) start = weekStart(firsts[0]);
  }
  if (diffDays(end, start) / 7 > maxWeeks - 1) start = addDays(end, -7 * (maxWeeks - 1));
  const out: TimelinePoint[] = [];
  const hoursByWeek = new Map<string, { m: number; b: number }>();
  for (const h of hours) {
    const x = hoursByWeek.get(h.week) ?? { m: 0, b: 0 };
    x.m += h.minutes;
    x.b += h.billable;
    hoursByWeek.set(h.week, x);
  }
  for (let w = start; w <= end; w = addDays(w, 7)) {
    const we = addDays(w, 6);
    const created = visible.filter((t) => t.createdOn && t.createdOn >= w && t.createdOn <= we).length;
    const done = visible.filter((t) => t.doneOn && t.doneOn >= w && t.doneOn <= we && t.statusGroup !== 'active').length;
    const total = visible.filter((t) => !t.createdOn || t.createdOn <= we).length;
    const finished = visible.filter((t) => t.statusGroup !== 'active' && t.doneOn && t.doneOn <= we).length;
    const hw = hoursByWeek.get(w);
    out.push({ week: w, created, done, total, finished, minutes: hw?.m ?? 0, billable: hw?.b ?? 0 });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Timeline bars (Gantt) and lists                                      */
/* ------------------------------------------------------------------ */

export interface GanttItem {
  id: string;
  ref: string;
  title: string;
  group: string;
  stage: TaskStage;
  start: ISODate;
  end: ISODate;
  /** the end is the due date (otherwise completion or today) */
  hasDue: boolean;
  overdue: boolean;
}

/** Tasks with a start or due date as bars, grouped by a dimension; open tasks without due date end today. */
export function ganttItems(tasks: AnalysisTask[], groupDim: AnalysisDim, opts: AnalysisOptions, limit = 400): GanttItem[] {
  const items: GanttItem[] = [];
  for (const t of tasks) {
    if (t.archived || (!t.startDate && !t.dueDate)) continue;
    const stage = taskStage(t.status, t.statusGroup);
    const start = t.startDate ?? t.createdOn ?? t.dueDate!;
    const end = t.dueDate ?? (stage === 'done' && t.doneOn ? t.doneOn : opts.today);
    items.push({
      id: t.id,
      ref: t.ref,
      title: t.title,
      group: taskDimValue(t, groupDim, opts.config),
      stage,
      start: start <= end ? start : end,
      end: start <= end ? end : start,
      hasDue: !!t.dueDate,
      overdue: stage !== 'done' && !!t.dueDate && t.dueDate < opts.today,
    });
  }
  const sort = dimSort(groupDim);
  items.sort((a, b) => sort(a.group, b.group) || a.start.localeCompare(b.start) || naturalCompare(a.ref, b.ref));
  return items.slice(0, limit);
}

/** Open tasks past their due date, the longest overdue first. */
export function overdueTasks(tasks: AnalysisTask[], today: ISODate, limit = 10) {
  return tasks
    .filter((t) => !t.archived && t.statusGroup === 'active' && t.dueDate && t.dueDate < today)
    .map((t) => ({ task: t, days: diffDays(today, t.dueDate!) }))
    .sort((a, b) => b.days - a.days)
    .slice(0, limit);
}

/** Hours per person and value of a dimension (who works where), in the period. */
export function personMatrix(tasks: AnalysisTask[], hours: AnalysisHours[], colDim: AnalysisDim, opts: AnalysisOptions) {
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  const cols = new Map<string, number>();
  const rows = new Map<string, Map<string, number>>();
  for (const h of hours) {
    if (!hoursInPeriod(h, opts.period)) continue;
    const t = taskMap.get(h.taskId);
    if (!t) continue;
    const v = colDim === 'person' ? h.personId : taskDimValue(t, colDim, opts.config) || NONE;
    cols.set(v, (cols.get(v) ?? 0) + h.minutes);
    const r = rows.get(h.personId) ?? new Map<string, number>();
    r.set(v, (r.get(v) ?? 0) + h.minutes);
    rows.set(h.personId, r);
  }
  const sort = dimSort(colDim);
  const colKeys = [...cols.keys()].sort((a, b) => sort(a === NONE ? '' : a, b === NONE ? '' : b));
  return {
    cols: colKeys.map((k) => ({ key: k, minutes: cols.get(k)! })),
    rows: [...rows.entries()]
      .map(([personId, m]) => ({ personId, total: [...m.values()].reduce((a, b) => a + b, 0), cells: colKeys.map((k) => m.get(k) ?? 0) }))
      .sort((a, b) => b.total - a.total),
  };
}

/** Dimensions that hold values in the data (for the filter row), with their distinct values. */
export function dimValues(tasks: AnalysisTask[], dim: AnalysisDim, config: AnalysisConfig): string[] {
  const set = new Set<string>();
  let none = false;
  for (const t of tasks) {
    const v = taskDimValue(t, dim, config);
    if (v) set.add(v);
    else none = true;
  }
  const values = [...set].sort(dimSort(dim));
  return none && values.length ? [...values, NONE] : values;
}
